/**
 * The object UI's React hooks: thin readers over the adapter and the pure
 * helpers beside them. Every read is sequenced (an older answer never
 * overwrites a newer one) and every commit tells the readers to read again.
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";

import { ApiError } from "../../data/api/errors";
import type {
  CommandBody,
  CommandEndpoint,
} from "../../data/object-ui-adapter";
import { useObjectUiAdapter } from "../../data/ObjectUiAdapterContext";
import { CommandRun, type CommandRunSnapshot } from "./command-run";
import {
  consignmentCandidate,
  consignmentMatches,
  findRecordGroup,
  tharsternMatch,
  tharsternNumberCandidate,
  THARSTERN_NUMBER_OBJECTS,
  type FindConsignmentMatch,
  type FindRecordGroup,
  type FindTharsternMatch,
} from "./find";
import type {
  ChildRow,
  Command,
  ManifestList,
  ManifestObject,
  Paginated,
  RecordView,
} from "./contract";
import {
  nameFromMatches,
  nameFromRows,
  type EntityFilterTarget,
} from "./entity-filter";
import { ownKindQuery } from "./list-includes";
import {
  emptyListQuery,
  parseListQuery,
  queueQuery,
  type ListQuery,
} from "./list-query";
import { useObjectManifest } from "./manifest";
import {
  presentRelatedRows,
  RELATED_ROWS_SHOWN,
  relatedListQuery,
  type PresentedChild,
  type PresentedRelated,
} from "./record";
import {
  firstTimelinePage,
  hasMoreTimeline,
  timelineQuery,
  withNextTimelinePage,
  type TimelineSearch,
  type TimelineShown,
} from "./timeline";
import {
  workThroughState,
  type WorkThroughParams,
  type WorkThroughState,
} from "./work-through";

// ── "Something changed": every commit re-reads what is on screen ─────────

const changeListeners = new Set<() => void>();
let changeVersion = 0;

/** Called after any commit: lists, records, counts and work-throughs read again. */
export function notifyObjectUiChanged(): void {
  changeVersion += 1;
  changeListeners.forEach((l) => l());
}

/** The number of commits so far: a reader that depends on it reads again after any command. */
export function useChangeVersion(): number {
  return useSyncExternalStore(
    (listener) => {
      changeListeners.add(listener);
      return () => {
        changeListeners.delete(listener);
      };
    },
    () => changeVersion,
  );
}

// ── Reads ────────────────────────────────────────────────────────────────

export type Read<T> =
  | { status: "loading" }
  | { status: "ready"; value: T }
  | { status: "error"; httpStatus: number | null };

/** One sequenced read, re-run when its key or the change version moves. */
function useRead<T>(
  key: string | null,
  load: () => Promise<T>,
  label: string,
): Read<T> & { reload: () => void } {
  const version = useChangeVersion();
  const [nonce, setNonce] = useState(0);
  const [read, setRead] = useState<Read<T>>({ status: "loading" });
  const seq = useRef(0);
  const lastKey = useRef<string | null>(null);
  const loadRef = useRef(load);
  loadRef.current = load;
  useEffect(() => {
    if (key === null) return;
    const mine = ++seq.current;
    // Keep what is shown while re-reading the same thing (after a commit); a new key starts afresh.
    const isSameKey = lastKey.current === key;
    lastKey.current = key;
    setRead((current) =>
      isSameKey && current.status === "ready" ? current : { status: "loading" },
    );
    loadRef
      .current()
      .then((value) => {
        if (mine === seq.current) setRead({ status: "ready", value });
      })
      .catch((err: unknown) => {
        if (mine !== seq.current) return;
        const httpStatus = err instanceof ApiError ? err.status : null;
        console.error(`object_ui.${label}.failed`, { key, status: httpStatus });
        setRead({ status: "error", httpStatus });
      });
  }, [key, version, nonce, label]);
  const reload = useCallback(() => setNonce((n) => n + 1), []);
  return { ...read, reload };
}

/**
 * Find on a courier consignment number: once the text has stopped changing, the orders whose despatch records
 * carry it, as options that open them. Empty for text that is not a candidate, while the lookup is out, and when
 * it fails (Find still offers everything else).
 */
export function useFindConsignment(
  text: string,
  debounceMs = 300,
): FindConsignmentMatch[] {
  const adapter = useObjectUiAdapter();
  const candidate = consignmentCandidate(text);
  const [found, setFound] = useState<{
    reference: string;
    matches: FindConsignmentMatch[];
  } | null>(null);
  useEffect(() => {
    if (candidate === null) return;
    let isCurrent = true;
    const timer = setTimeout(() => {
      adapter
        .findOrdersByConsignment(candidate)
        .then((answer) => {
          if (isCurrent)
            setFound({
              reference: candidate,
              matches: consignmentMatches(candidate, answer.orders),
            });
        })
        .catch((err: unknown) => {
          console.error("object_ui.find_consignment.failed", {
            status: err instanceof ApiError ? err.status : null,
          });
        });
    }, debounceMs);
    return () => {
      isCurrent = false;
      clearTimeout(timer);
    };
  }, [adapter, candidate, debounceMs]);
  return candidate !== null && found?.reference === candidate
    ? found.matches
    : [];
}

/**
 * Find on a Tharstern number (three to six digits): once the text has stopped changing, each Tharstern record the
 * number names (a sales order, job or estimate number), as options that open it. Reads each object's record route,
 * so a number the caller's customers do not reach, or that is not in the copy, offers nothing. Empty for other
 * text, for an object that is switched off here, while the lookup is out, and when it fails.
 */
export function useFindTharsternRecords(
  text: string,
  debounceMs = 300,
): FindTharsternMatch[] {
  const adapter = useObjectUiAdapter();
  const index = useObjectManifest();
  const candidate = tharsternNumberCandidate(text);
  const [found, setFound] = useState<{
    number: string;
    matches: FindTharsternMatch[];
  } | null>(null);
  useEffect(() => {
    if (candidate === null) return;
    let isCurrent = true;
    const timer = setTimeout(() => {
      const objects = THARSTERN_NUMBER_OBJECTS.filter((id) =>
        index.isObjectEnabled(id),
      ).map((id) => index.objectOf(id));
      Promise.all(
        objects.map(async (object) => {
          if (!object.record) return null;
          try {
            return tharsternMatch(
              object,
              await adapter.getRecord(object.record, candidate),
            );
          } catch (err: unknown) {
            // A 404 is the answer for a number the object does not hold; anything else is logged.
            if (!(err instanceof ApiError && err.status === 404))
              console.error("object_ui.find_tharstern.failed", {
                object: object.id,
                status: err instanceof ApiError ? err.status : null,
              });
            return null;
          }
        }),
      ).then((matches) => {
        if (isCurrent)
          setFound({
            number: candidate,
            matches: matches.filter((m): m is FindTharsternMatch => m !== null),
          });
      });
    }, debounceMs);
    return () => {
      isCurrent = false;
      clearTimeout(timer);
    };
  }, [adapter, index, candidate, debounceMs]);
  return candidate !== null && found?.number === candidate ? found.matches : [];
}

/** At most this many list searches are out at once: Find's reads queue behind each other, as Claude's `find` does. */
export const FIND_CONCURRENCY = 4;

/** A list that refuses the search is no match rather than a failure: the person may not read it, or it takes no `q`. */
const FIND_NOT_A_MATCH_STATUSES = new Set([401, 403, 404, 422]);

/** What Find's text search has found so far. */
export interface FindRecordsRead {
  /** The text the groups are for; empty while nothing is searched. */
  text: string;
  /** The objects that matched, in the order searched; one appears as its answer arrives. */
  groups: FindRecordGroup[];
  /** The objects whose search failed (not a refusal): the page says they were not searched. */
  failed: ManifestObject[];
  isSearching: boolean;
}

const NOT_SEARCHING: FindRecordsRead = {
  text: "",
  groups: [],
  failed: [],
  isSearching: false,
};

/** Runs `run` over the items, at most `limit` at a time, starting no more once `isCurrent` turns false. */
async function eachLimited<T>(
  items: readonly T[],
  limit: number,
  isCurrent: () => boolean,
  run: (item: T, at: number) => Promise<void>,
): Promise<void> {
  let next = 0;
  const worker = async () => {
    while (isCurrent() && next < items.length) {
      const at = next++;
      await run(items[at]!, at);
    }
  };
  await Promise.all(
    Array.from({ length: Math.min(limit, items.length) }, worker),
  );
}

/**
 * Find's text search (FB-053): once the text has stopped changing, each object's list searched for it through the
 * list's own `q`, the first `perObject` rows of each, at most `FIND_CONCURRENCY` reads out at once and in the order
 * the objects are given, so the objects people look up most answer first. A newer text drops the older one's answers
 * and starts none of its queued reads. `objects` must already be only those on for the person.
 */
export function useFindRecords(
  text: string,
  objects: readonly ManifestObject[],
  { perObject, debounceMs = 300 }: { perObject: number; debounceMs?: number },
): FindRecordsRead {
  const adapter = useObjectUiAdapter();
  const index = useObjectManifest();
  const wanted = text.trim();
  const objectsKey = objects.map((o) => o.id).join(",");
  const searchKey =
    wanted === "" ? null : `${wanted}|${perObject}|${objectsKey}`;
  const objectsRef = useRef(objects);
  objectsRef.current = objects;
  const [found, setFound] = useState<{
    key: string;
    read: FindRecordsRead;
  } | null>(null);
  useEffect(() => {
    if (searchKey === null) return;
    let isCurrent = true;
    const searched = objectsRef.current;
    const timer = setTimeout(() => {
      const answers: (FindRecordGroup | null)[] = searched.map(() => null);
      const failed: ManifestObject[] = [];
      let outstanding = searched.length;
      const publish = () => {
        if (!isCurrent) return;
        setFound({
          key: searchKey,
          read: {
            text: wanted,
            groups: answers.filter(
              (g): g is FindRecordGroup => g !== null && g.hits.length > 0,
            ),
            failed: [...failed],
            isSearching: outstanding > 0,
          },
        });
      };
      publish();
      void eachLimited(
        searched,
        FIND_CONCURRENCY,
        () => isCurrent,
        async (object, at) => {
          try {
            const page = await adapter.list(object.list!, {
              ...emptyListQuery(),
              q: wanted,
              page_size: perObject,
            });
            answers[at] = findRecordGroup(index, object, wanted, page);
          } catch (err: unknown) {
            const status = err instanceof ApiError ? err.status : null;
            if (status === null || !FIND_NOT_A_MATCH_STATUSES.has(status)) {
              failed.push(object);
              console.error("object_ui.find_records.failed", {
                object: object.id,
                status,
              });
            }
          }
          outstanding -= 1;
          publish();
        },
      );
    }, debounceMs);
    return () => {
      isCurrent = false;
      clearTimeout(timer);
    };
  }, [adapter, index, searchKey, wanted, perObject, debounceMs]);
  if (searchKey === null) return NOT_SEARCHING;
  return found?.key === searchKey
    ? found.read
    : { ...NOT_SEARCHING, text: wanted, isSearching: true };
}

export function useObjectList(object: ManifestObject, query: ListQuery) {
  const adapter = useObjectUiAdapter();
  const list = object.list;
  const key = list ? `${object.id}?${JSON.stringify(query)}` : null;
  return useRead<Paginated<ChildRow>>(
    key,
    () => adapter.list(list!, query),
    "list",
  );
}

/**
 * On a list that also holds a second object, the list's own rows alone: their count and the first of them, which
 * "Work through these" walks (the second object's rows are not records of this object). Reads nothing while
 * `isNeeded` is false, so a list that holds one object, or is already only its own rows, pays no second read.
 */
export function useOwnKindFirstRow(
  object: ManifestObject,
  query: ListQuery,
  isNeeded: boolean,
) {
  const adapter = useObjectUiAdapter();
  const list = object.list;
  const own = list && isNeeded ? ownKindQuery(list, query) : null;
  const firstOnly = own ? { ...own, page: 1, page_size: 1 } : null;
  const key = firstOnly
    ? `${object.id}?own?${JSON.stringify(firstOnly)}`
    : null;
  return useRead<Paginated<ChildRow>>(
    key,
    () => adapter.list(list!, firstOnly!),
    "list_own_kind",
  );
}

export function useObjectRecord(object: ManifestObject, recordKey: string) {
  const adapter = useObjectUiAdapter();
  const record = object.record;
  const key = record ? `${object.id}/${recordKey}` : null;
  return useRead<RecordView>(
    key,
    () => adapter.getRecord(record!, recordKey),
    "record",
  );
}

/**
 * Tells the object's `on_open` route, once, that a person opened this record
 * (a ticket's first-opened stamp). Waits for `isShown`, so a record the
 * person was refused is never stamped; never blocks the render; a failure is
 * logged and swallowed. Claude's tools never run this: it lives in the shell.
 */
export function useRecordOpened(
  object: ManifestObject,
  recordKey: string,
  isShown: boolean,
): void {
  const adapter = useObjectUiAdapter();
  const record = object.record;
  const hasFired = useRef(false);
  useEffect(() => {
    if (!isShown || hasFired.current || !record?.on_open) return;
    hasFired.current = true;
    adapter.recordOpened(record, recordKey).catch((err: unknown) => {
      console.warn("object_ui.record_opened.failed", {
        object: object.id,
        key: recordKey,
        status: err instanceof ApiError ? err.status : null,
      });
    });
  }, [adapter, object.id, record, recordKey, isShown]);
}

export type TimelineRead =
  | { status: "loading" }
  | { status: "error"; httpStatus: number | null }
  | {
      status: "ready";
      shown: TimelineShown;
      /** "Show more" is reading the next page. */
      isLoadingMore: boolean;
      /** The last "Show more" failed; what is shown stays. */
      hasMoreFailed: boolean;
    };

/**
 * The record's timeline: the first page for the search, read again when the
 * search changes or after any command, and `showMore` appending the next page.
 * Sequenced: a page for an older search never lands on a newer one.
 */
export function useTimeline(
  object: ManifestObject,
  recordKey: string,
  search: TimelineSearch,
): TimelineRead & { showMore: () => void } {
  const adapter = useObjectUiAdapter();
  const version = useChangeVersion();
  const record = object.record;
  const searchKey = JSON.stringify(timelineQuery(search, 1));
  const [read, setRead] = useState<TimelineRead>({ status: "loading" });
  const seq = useRef(0);
  const lastKey = useRef<string | null>(null);
  const searchRef = useRef(search);
  searchRef.current = search;
  const readRef = useRef(read);
  readRef.current = read;

  useEffect(() => {
    if (!record?.timeline_path) return;
    const mine = ++seq.current;
    const key = `${object.id}/${recordKey}?${searchKey}`;
    // Keep what is shown while re-reading after a command; a new search starts afresh.
    const isSameKey = lastKey.current === key;
    lastKey.current = key;
    setRead((current) =>
      isSameKey && current.status === "ready" ? current : { status: "loading" },
    );
    adapter
      .getTimeline(record, recordKey, timelineQuery(searchRef.current, 1))
      .then((answer) => {
        if (mine === seq.current)
          setRead({
            status: "ready",
            shown: firstTimelinePage(answer),
            isLoadingMore: false,
            hasMoreFailed: false,
          });
      })
      .catch((err: unknown) => {
        if (mine !== seq.current) return;
        const httpStatus = err instanceof ApiError ? err.status : null;
        console.error("object_ui.timeline.failed", {
          object: object.id,
          key: recordKey,
          status: httpStatus,
        });
        setRead({ status: "error", httpStatus });
      });
  }, [adapter, object.id, record, recordKey, searchKey, version]);

  const showMore = useCallback(() => {
    const current = readRef.current;
    if (
      !record?.timeline_path ||
      current.status !== "ready" ||
      current.isLoadingMore ||
      !hasMoreTimeline(current.shown)
    )
      return;
    const mine = seq.current;
    setRead({ ...current, isLoadingMore: true, hasMoreFailed: false });
    adapter
      .getTimeline(
        record,
        recordKey,
        timelineQuery(searchRef.current, current.shown.page + 1),
      )
      .then((answer) => {
        if (mine !== seq.current) return;
        setRead((now) =>
          now.status === "ready"
            ? {
                ...now,
                shown: withNextTimelinePage(now.shown, answer),
                isLoadingMore: false,
              }
            : now,
        );
      })
      .catch((err: unknown) => {
        if (mine !== seq.current) return;
        console.error("object_ui.timeline_more.failed", {
          object: object.id,
          key: recordKey,
          status: err instanceof ApiError ? err.status : null,
        });
        setRead((now) =>
          now.status === "ready"
            ? { ...now, isLoadingMore: false, hasMoreFailed: true }
            : now,
        );
      });
  }, [adapter, object.id, record, recordKey]);

  return { ...read, showMore };
}

/**
 * The name to show for an entity filter's value that came without one (a link, a related list): from the filtered
 * rows when they name it, else by searching the named object's list for it. Null while unknown, while nothing is
 * chosen, or when neither finds it (the picker then shows the value itself).
 */
export function useEntityFilterName(
  list: ManifestList,
  target: EntityFilterTarget | null,
  value: string | null,
  rows: readonly ChildRow[] | null,
): string | null {
  const adapter = useObjectUiAdapter();
  const fromRows =
    target && value && rows ? nameFromRows(list, target, rows) : null;
  // Search only once the rows are in and do not name it: the rows answer most links without a second read.
  const key =
    target && value && rows && fromRows === null
      ? `${target.object.id}?name?${target.valueField}=${value}`
      : null;
  const searched = useRead<string | null>(
    key,
    async () => {
      const page = await adapter.list(target!.list, {
        ...emptyListQuery(),
        q: value,
        filters: target!.filters,
        page_size: 20,
      });
      return nameFromMatches(target!, page.items, value!);
    },
    "entity_filter_name",
  );
  return fromRows ?? (searched.status === "ready" ? searched.value : null);
}

/**
 * The names of the several records a many-valued entity filter holds, each found by searching the named object's list
 * for its value (the filtered rows name one record, so they cannot tell several apart). A value not found is absent,
 * and the chip shows the value itself. Empty while loading.
 */
export function useEntityFilterNames(
  target: EntityFilterTarget | null,
  values: readonly string[],
): Record<string, string> {
  const adapter = useObjectUiAdapter();
  const key =
    target && values.length > 0
      ? `${target.object.id}?names?${target.valueField}=${values.join(",")}`
      : null;
  const names = useRead<Record<string, string>>(
    key,
    async () => {
      const found = await Promise.all(
        values.map(async (value) => {
          const page = await adapter.list(target!.list, {
            ...emptyListQuery(),
            q: value,
            filters: target!.filters,
            page_size: 20,
          });
          return [value, nameFromMatches(target!, page.items, value)] as const;
        }),
      );
      return Object.fromEntries(
        found.filter((entry): entry is [string, string] => entry[1] !== null),
      );
    },
    "entity_filter_names",
  );
  return names.status === "ready" ? names.value : {};
}

export function useCollectionCommands(object: ManifestObject) {
  const adapter = useObjectUiAdapter();
  const index = useObjectManifest();
  const hasAny = index.collectionCommandsOf(object.id).length > 0;
  return useRead<Command[]>(
    hasAny ? object.id : null,
    () => adapter.listCollectionCommands(object.id),
    "collection_commands",
  );
}

/** A collection command a record offers on one of its related lists (Add purchase invoice… on a purchase order). */
export interface RelatedCollectionCommand {
  command: Command;
  related: PresentedRelated;
}

/**
 * The collection commands of every related list a record has, in the order the lists are presented (each object's
 * commands in its owner's order), so the record's header can draw them with its own commands. A command two lists
 * offer appears once, under the first. Empty while they load; a list whose commands can't be read offers none.
 */
export function useRelatedCollectionCommands(
  relatedLists: readonly PresentedRelated[],
): RelatedCollectionCommand[] {
  const adapter = useObjectUiAdapter();
  const index = useObjectManifest();
  const withCommands = relatedLists.filter(
    (related) => index.collectionCommandsOf(related.object).length > 0,
  );
  const read = useRead<RelatedCollectionCommand[]>(
    withCommands.length > 0
      ? `related_commands?${withCommands.map((related) => related.object).join(",")}`
      : null,
    async () => {
      const perList = await Promise.all(
        withCommands.map((related) =>
          adapter
            .listCollectionCommands(related.object)
            .then((commands) =>
              commands.map((command) => ({ command, related })),
            )
            .catch((): RelatedCollectionCommand[] => {
              console.error("object_ui.related_commands.failed", {
                object: related.object,
              });
              return [];
            }),
        ),
      );
      const seen = new Set<string>();
      return perList.flat().filter(({ command }) => {
        if (seen.has(command.name)) return false;
        seen.add(command.name);
        return true;
      });
    },
    "related_commands",
  );
  return read.status === "ready" ? read.value : [];
}

/**
 * A record's related list as its first rows and how many it has in all (Ben's R39: a count and a few rows, never a sum
 * or a value): one read of the target list for exactly the query its link opens (`relatedListQuery`), three rows a
 * page. Each related list reads on its own, so the record renders at once and its lists fill in as they arrive. An
 * error says why: 403 where the person cannot see that list. Re-read after any commit, like every list.
 */
export function useRelatedRows(
  related: PresentedRelated,
): Read<PresentedChild> & { reload: () => void } {
  const adapter = useObjectUiAdapter();
  const index = useObjectManifest();
  const list = index.objectOf(related.object).list;
  const query = list
    ? relatedListQuery(list, related, RELATED_ROWS_SHOWN)
    : null;
  return useRead<PresentedChild>(
    query ? `${related.object}?related?${JSON.stringify(query)}` : null,
    () =>
      adapter
        .list(list!, query!)
        .then((page) => presentRelatedRows(index, related, page)),
    "related_rows",
  );
}

/**
 * Each counted queue's size: the list's `total` with the queue's params, one
 * row per page (no counts endpoint, platform spec decision 9). Null while
 * unknown or when the count could not be read.
 */
export function useQueueCounts(
  object: ManifestObject,
): Record<string, number | null> {
  const adapter = useObjectUiAdapter();
  const version = useChangeVersion();
  const counted = useMemo(
    () => object.list?.queues.filter((q) => q.count) ?? [],
    [object],
  );
  const [counts, setCounts] = useState<Record<string, number | null>>({});
  useEffect(() => {
    const list = object.list;
    if (!list || counted.length === 0) return;
    let isCurrent = true;
    Promise.all(
      counted.map((queue) =>
        adapter
          .list(list, { ...queueQuery(queue), page_size: 1 })
          .then((page) => [queue.id, page.total] as const)
          .catch(() => [queue.id, null] as const),
      ),
    ).then((entries) => {
      if (isCurrent) setCounts(Object.fromEntries(entries));
    });
    return () => {
      isCurrent = false;
    };
  }, [adapter, object, counted, version]);
  return counts;
}

/** Where Previous and Next go while working through a list (null when not working through). */
export function useWorkThrough(
  object: ManifestObject,
  params: WorkThroughParams | null,
  recordKey: string,
): Read<WorkThroughState> {
  const adapter = useObjectUiAdapter();
  const list = object.list;
  const key =
    params && list ? `${params.through}#${params.at}#${recordKey}` : null;
  const read = useRead<WorkThroughState>(
    key,
    async () => {
      const query = parseListQuery(params!.through, list!);
      const at = params!.at;
      const keyAt = async (
        position: number,
      ): Promise<{ total: number; key: string | null }> => {
        if (position < 1) return { total: -1, key: null };
        const page = await adapter.list(list!, {
          ...query,
          page: position,
          page_size: 1,
        });
        const item = page.items[0];
        return {
          total: page.total,
          key: item ? String(item[object.identifier.field]) : null,
        };
      };
      const [before, here, after] = await Promise.all([
        keyAt(at - 1),
        keyAt(at),
        keyAt(at + 1),
      ]);
      return workThroughState(at, recordKey, {
        total: here.total,
        keyBefore: before.key,
        keyAt: here.key,
        keyAfter: after.key,
      });
    },
    "work_through",
  );
  return read;
}

// ── Commands ─────────────────────────────────────────────────────────────

/**
 * One command run for as long as the shell is open. The run is created once
 * per endpoint and disposed on close; its snapshot drives the shell.
 */
export function useCommandRun(
  endpoint: CommandEndpoint,
  options: {
    initialBody?: CommandBody;
    lockedPaths?: readonly string[];
    ownedPaths?: readonly string[];
  } = {},
): { run: CommandRun; snapshot: CommandRunSnapshot } {
  const adapter = useObjectUiAdapter();
  const [run] = useState(
    () =>
      new CommandRun({
        adapter,
        endpoint,
        ...(options.initialBody ? { initialBody: options.initialBody } : {}),
        ...(options.lockedPaths ? { lockedPaths: options.lockedPaths } : {}),
        ...(options.ownedPaths ? { ownedPaths: options.ownedPaths } : {}),
      }),
  );
  useEffect(() => {
    run.start();
    return () => run.dispose();
  }, [run]);
  const snapshot = useSyncExternalStore(run.subscribe, run.getSnapshot);
  return { run, snapshot };
}
